/**
 * Starlink kit ordering labels — shared client/server.
 *
 * A Starlink kit is ordered against a SPEED PLAN (100 / 200 / uncapped) and
 * against who manages the service: Techery-managed (we hold the Starlink
 * account and rebill) or self-managed ("S/M" — the customer's own Starlink
 * account). Provisioning needs both on the card, because the two together
 * decide which kit gets ordered:
 *
 *   STARLINK KIT - 100        STARLINK S/M KIT - 100
 *   STARLINK KIT - 200        STARLINK S/M KIT - 200
 *   STARLINK KIT - MAX        STARLINK S/M KIT - MAX
 *                             STARLINK S/M KIT
 *
 * Pure string/label logic only — no Firestore, no React. The provisioning
 * item stores the two axes (`starlinkSpeedTier` + `starlinkSelfManaged`) and
 * every surface renders them through `formatStarlinkKitLabel` so the wording
 * can never drift between the deal card, the ordering queue and the drawer.
 */

/** Speed plan the kit is destined for. `MAX` = uncapped / full speed. */
export type StarlinkSpeedTier = '100' | '200' | 'MAX';

export const STARLINK_SPEED_TIERS: readonly StarlinkSpeedTier[] = ['100', '200', 'MAX'];

/** Human wording for the tier — used in tooltips and detail rows. */
export const STARLINK_SPEED_TIER_LABELS: Record<StarlinkSpeedTier, string> = {
  '100': '100 Mbps capped',
  '200': '200 Mbps capped',
  MAX: 'Full speed (uncapped)',
};

export function isStarlinkSpeedTier(value: unknown): value is StarlinkSpeedTier {
  return typeof value === 'string' && (STARLINK_SPEED_TIERS as readonly string[]).includes(value);
}

/**
 * Does this provisioning item name describe a Starlink kit?
 *
 * Deliberately looser than an exact `'starlink kit'` substring: real item
 * names come off the parts catalog and quote lines as "Starlink Gen 3 Kit",
 * "Starlink Kit (Mini)", "STARLINK KIT - 100". Matching "starlink" AND "kit"
 * catches all of them without also catching "Starlink Mounting Pole".
 */
export function isStarlinkKitItemName(itemName?: string | null): boolean {
  if (!itemName) return false;
  const normalized = itemName.toLowerCase();
  return normalized.includes('starlink') && normalized.includes('kit');
}

/**
 * Read the speed tier out of a service-plan name.
 *
 * Tenant plan names carry the cap in the text — "Starlink Small Plan Max
 * 100Mbps", "Starlink Medium Plan Max 200Mbps", "Starlink Small Plan Full
 * Speed". The explicit number wins over the word "max": "…Plan Max 100Mbps"
 * is the 100 tier, not the uncapped one. Returns null when the name carries
 * no readable cap (e.g. "Starlink Residential") — the caller then leaves the
 * tier unset rather than guessing.
 */
export function parseStarlinkSpeedTier(planName?: string | null): StarlinkSpeedTier | null {
  if (!planName) return null;
  const normalized = planName.toLowerCase();

  // Whole-number tokens only — "1000" must not read as "100".
  const numbers: string[] = normalized.match(/\d+/g) ?? [];
  if (numbers.includes('200')) return '200';
  if (numbers.includes('100')) return '100';

  if (/full\s*speed|uncapped|unlimited|no\s*speed\s*cap|\bmax\b/.test(normalized)) return 'MAX';
  return null;
}

/**
 * The ordering label shown on the provisioning card, e.g.
 * `STARLINK S/M KIT - 200`. Falls back to the bare kit name when the tier
 * is unknown, so an un-set card still reads correctly rather than showing a
 * dangling separator.
 */
export function formatStarlinkKitLabel(opts: {
  speedTier?: StarlinkSpeedTier | null;
  selfManaged?: boolean | null;
}): string {
  const base = opts.selfManaged ? 'STARLINK S/M KIT' : 'STARLINK KIT';
  return opts.speedTier ? `${base} - ${opts.speedTier}` : base;
}

/**
 * Longer form for tooltips / read-only summaries — "Self-managed · 200 Mbps
 * capped". Returns null when neither axis is known.
 */
export function describeStarlinkKitPlan(opts: {
  speedTier?: StarlinkSpeedTier | null;
  selfManaged?: boolean | null;
}): string | null {
  const parts: string[] = [];
  if (opts.selfManaged != null) parts.push(opts.selfManaged ? 'Self-managed service' : 'Techery-managed service');
  if (opts.speedTier) parts.push(STARLINK_SPEED_TIER_LABELS[opts.speedTier]);
  return parts.length > 0 ? parts.join(' · ') : null;
}
